import Checkbox from 'components/Checkbox';
import Modal from 'components/Modal';
import { IServiceCategory } from 'features/cashier/services/types/api';
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
type IModalAddonServicesProps = {};
type IModalAddonServicesRef = {
  open: (parentService: IServiceCategory, addonServices: IServiceCategory[]) => Promise<IServiceCategory[]>;
  close: () => void;
};
export const ModalAddonServicesRef = React.createRef<IModalAddonServicesRef>();
const ModalAddonServicesContext = forwardRef<IModalAddonServicesRef, IModalAddonServicesProps>((_props, ref) => {
  const [visible, setOpen] = useState(false);
  const [addonServices, setAddonServices] = useState<IServiceCategory[]>([]);
  const [selectedServices, setSelectedServices] = useState<IServiceCategory[]>([]);
  const [parentService, setParentService] = useState<IServiceCategory | null>(null);
  const callbackResolve = useRef<(services: IServiceCategory[]) => void>(() => undefined);
  const handleOpen: IModalAddonServicesRef['open'] = (parentService, services) => {
    callbackResolve.current?.([]);
    setParentService(parentService);
    setAddonServices(services);
    setSelectedServices([]);
    setOpen(true);
    return new Promise((resolve) => {
      callbackResolve.current = resolve;
    });
  };

  const handleClose: IModalAddonServicesRef['close'] = () => {
    callbackResolve.current?.([]);
    setOpen(false);
  };

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  const isSelected = (item: IServiceCategory) => selectedServices.some((s) => s.id === item.id);

  const toggle = (item: IServiceCategory) =>
    setSelectedServices((prev) =>
      prev.some((s) => s.id === item.id) ? prev.filter((s) => s.id !== item.id) : [...prev, item],
    );

  const handleSubmit = () => {
    callbackResolve.current?.(selectedServices);
    handleClose();
  };

  return (
    <Modal
      visible={visible}
      width={900}
      v2
      modalTitle={'SERVICE ADD-ON'}
      okTitle='OK'
      cancelTitle='Close'
      onClose={handleClose}
      onSubmit={handleSubmit}
      noneBodyStyle
      noneFooterStyle
      containerPadding={0}
    >
      <BodyStyled>
        {parentService && (
          <AmountSection>
            <p className='name'>{parentService.serviceName}</p>
            <p className='price'>{formatCurrency(parentService.priceSell)}</p>
          </AmountSection>
        )}
        <AddonGrid>
          {addonServices.map((item) => (
            <AddonRow key={item.id} onClick={() => toggle(item)}>
              <Checkbox size='large' value={isSelected(item)} />
              <span className='name'>{item.serviceName}</span>
              <span className='price'>+{formatCurrency(item.priceSell)}</span>
            </AddonRow>
          ))}
        </AddonGrid>
      </BodyStyled>
    </Modal>
  );
});

ModalAddonServicesContext.displayName = 'ModalAddonServicesContext';
const ModalAddonServices = {
  Portal: () => <ModalAddonServicesContext ref={ModalAddonServicesRef} />,
  open: (parentService: IServiceCategory, services: IServiceCategory[]) => ModalAddonServicesRef.current?.open(parentService, services),
  close: () => ModalAddonServicesRef.current?.close(),
};

export default ModalAddonServices;

const BodyStyled = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: #fff;
`;

const AmountSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 16px 24px;
  background: #fff;
  .name {
    color: #2d6772;
    font-family: Poppins;
    font-size: 32px;
    font-weight: 600;
    line-height: normal;
  }
  .price {
    color: #1d2129;
    font-family: Poppins;
    font-size: 24px;
    font-weight: 500;
    line-height: normal;
  }
`;

const AddonGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding: 24px 24px 32px;
  background: #f6f7fc;
`;

const AddonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  .checkbox-container {
    flex-shrink: 0;
  }
  .name {
    flex: 1;
    color: #1d2129;
    font-family: Poppins;
    font-size: 18px;
    font-weight: 500;
    line-height: normal;
  }
  .price {
    color: #2d6772;
    font-family: Poppins;
    font-size: 18px;
    font-weight: 500;
    line-height: normal;
    white-space: nowrap;
  }
`;
